import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { createCheckoutSession } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Heart, Users as UsersIcon, Package, Sparkles, Lock } from "lucide-react";
import ParentalGate from "@/components/ParentalGate";

export default function StripeDialog({ open, onClose, playerId }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [donation, setDonation] = useState("5.00");
  const [loading, setLoading] = useState(false);
  const [gateOpen, setGateOpen] = useState(false);

  const PRODUCTS = [
    {
      lookup_key: "chronos_premium_pack",
      title: t("payment.product_premium_title"),
      subtitle: t("payment.product_premium_sub"),
      price: "4,99 €",
      icon: Package,
      accent: "text-amber-300",
      badge: "bg-amber-500/20 border-amber-400/40",
    },
    {
      lookup_key: "chronos_family_edition",
      title: t("payment.product_family_title"),
      subtitle: t("payment.product_family_sub"),
      price: "14,99 €",
      icon: UsersIcon,
      accent: "text-emerald-300",
      badge: "bg-emerald-500/20 border-emerald-400/40",
    },
    {
      lookup_key: "chronos_donation",
      title: t("payment.product_donation_title"),
      subtitle: t("payment.product_donation_sub"),
      price: t("payment.price_free"),
      icon: Heart,
      accent: "text-rose-300",
      badge: "bg-rose-500/20 border-rose-400/40",
    },
  ];

  if (!open) return null;

  const handleCheckout = async () => {
    if (!selected) return;
    // Show parental gate first — required by kids-app policies
    setGateOpen(true);
  };

  const runCheckout = async () => {
    setGateOpen(false);
    setLoading(true);
    try {
      const payload = {
        lookup_key: selected,
        origin_url: window.location.origin,
        player_id: playerId,
      };
      if (selected === "chronos_donation") {
        payload.donation_amount = parseFloat(donation);
      }
      const res = await createCheckoutSession(payload);
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("payment.checkout_error"));
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur"
        onClick={onClose}
        data-testid="stripe-dialog"
      >
        <motion.div
          initial={{ scale: 0.95, y: 15 }} animate={{ scale: 1, y: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="glass-strong rounded-3xl p-6 sm:p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xs uppercase tracking-[0.3em] text-amber-300 font-bold">{t("payment.dialog_kicker")}</div>
              <h3 className="mt-1 font-serif text-3xl font-black text-stone-50">{t("payment.dialog_title")}</h3>
              <p className="mt-1 text-sm text-stone-400 max-w-md">{t("payment.dialog_desc")}</p>
            </div>
            <button onClick={onClose} className="text-stone-400 hover:text-stone-100" data-testid="stripe-close">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mt-6 grid gap-3">
            {PRODUCTS.map((p) => {
              const Icon = p.icon;
              const active = selected === p.lookup_key;
              return (
                <button
                  key={p.lookup_key}
                  onClick={() => setSelected(p.lookup_key)}
                  data-testid={`product-${p.lookup_key}`}
                  className={`text-left rounded-2xl border-2 p-4 transition-all flex items-center gap-4 ${
                    active ? "border-amber-400 bg-amber-500/10" : "border-white/10 bg-black/30 hover:border-white/30"
                  }`}
                >
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center border ${p.badge}`}>
                    <Icon className={`w-6 h-6 ${p.accent}`} />
                  </div>
                  <div className="flex-1">
                    <div className="font-serif text-lg font-bold text-stone-50">{p.title}</div>
                    <div className="text-sm text-stone-400">{p.subtitle}</div>
                  </div>
                  <div className={`font-serif text-xl font-black ${p.accent}`}>{p.price}</div>
                </button>
              );
            })}
          </div>

          {selected === "chronos_donation" && (
            <div className="mt-4 flex items-center gap-3">
              <label className="text-xs uppercase tracking-widest font-bold text-stone-300">{t("payment.amount_label")}</label>
              <Input
                type="number" min="1" max="500" step="0.5"
                value={donation}
                onChange={(e) => setDonation(e.target.value)}
                className="w-32 bg-black/40 border-white/15 font-mono"
                data-testid="donation-amount"
              />
            </div>
          )}

          <div className="mt-6">
            <Button
              disabled={!selected || loading}
              onClick={handleCheckout}
              size="lg"
              className="w-full bg-amber-400 hover:bg-amber-300 text-stone-950 font-bold rounded-full py-6"
              data-testid="btn-stripe-checkout"
            >
              <Lock className="w-4 h-4 mr-2" />
              {loading ? t("payment.checkout_loading") : selected ? t("payment.checkout_btn") : t("payment.select_option")}
            </Button>
          </div>

          <div className="mt-4 flex items-center justify-between text-xs text-stone-500">
            <span className="flex items-center gap-1.5"><Sparkles className="w-3 h-3" /> {t("payment.test_card")}</span>
            <span>{t("payment.stripe_test_badge")}</span>
          </div>
        </motion.div>
      </motion.div>
      <ParentalGate open={gateOpen} onPass={runCheckout} onClose={() => setGateOpen(false)} />
    </AnimatePresence>
  );
}
